import { Flex, Icon, Spinner, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import { useForceDeliverBidMutation } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { requireBooleanEnvVar } from "../../../../config";

interface Props {
  bidId: string;
  isWaitingForSave: boolean;
}

// Local-development affordance: delivers the bid as if the Signicat ceremony had completed.
// The BFF runs the same deadline and completeness gates as real delivery; only the signing
// itself is skipped (its webhook can never reach a localhost BFF). Styled to match the buyer
// DEV split-button: a solid DEV badge fused to the action label.
export const DevForceDeliverBid = (props: Props) => {
  if (!requireBooleanEnvVar("VITE_ENABLE_DEV_TOOLS")) return null;
  return <ForceDeliverButton {...props} />;
};

const ForceDeliverButton = ({ bidId, isWaitingForSave }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const [forceDeliverBid, { isLoading }] = useForceDeliverBidMutation();
  const isDisabled = isLoading || isWaitingForSave;

  const handleDeliver = async () => {
    try {
      await forceDeliverBid({ bidId }).unwrap();
      toast({ title: t("Delivered without signing"), status: "success", duration: 4000 });
    } catch (e) {
      trigger(e);
    }
  };

  return (
    // Deliberate deviation from the semantic-token rule (see webapp/CLAUDE.md): the raw purple
    // palette marks this as a dev-only affordance that must never blend into the real UI; it mirrors
    // the buyer-side DevFastForwardMenu so the two read as one tool family.
    <Flex justify="flex-end" mt="3">
      <Flex
        as="button"
        onClick={handleDeliver}
        disabled={isDisabled}
        title={isWaitingForSave ? t("Wait until the latest changes are saved.") ?? undefined : undefined}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        align="stretch"
        h="8"
        border="1px solid"
        borderColor="purple.200"
        borderRadius="md"
        overflow="hidden"
        bg="white"
        opacity={isDisabled ? 0.6 : 1}
        _hover={{ borderColor: "purple.300" }}
        aria-label={t("Deliver without signing") ?? undefined}
      >
        <Flex align="center" px="2" bg="purple.600" color="white" fontFamily="mono" fontSize="2xs" fontWeight="bold" letterSpacing="wider">
          DEV
        </Flex>
        <Flex align="center" gap="2" px="3" fontSize="sm" fontWeight="semibold" color="purple.700">
          {isDisabled ? <Spinner boxSize="3.5" /> : <Icon as={FiCheck} boxSize="3.5" />}
          {t("Deliver without signing")}
        </Flex>
      </Flex>
    </Flex>
  );
};
